import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const TermsAndConditions: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-gray-900">
            {/* Header */}
            <header className="bg-gray-800 py-4">
                <div className="container mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center gap-3">
                    <img src="/images/logo.png" alt="Fixisy Logo" className="h-8 w-8 sm:h-10 sm:w-10 object-contain" />
                    <Link to="/" className="text-xl sm:text-2xl font-bold text-cyan-400">Fixisy</Link>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8">Terms and Conditions</h1>

                <p className="text-gray-300 text-sm sm:text-base mb-8">
                    Fixisy is a product offered and operated by <strong className="text-white">Kifayti Retail Private Limited</strong>.
                    References to &quot;Fixisy,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot; in these Terms mean Kifayti Retail Private Limited
                    in connection with the Fixisy platform.
                </p>

                <div className="prose prose-invert max-w-none text-sm sm:text-base">
                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
                        <p className="text-gray-300">
                            By accessing and using the Fixisy application (&quot;the App&quot;), a product of Kifayti Retail Private Limited,
                            you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
                            If you do not agree to these terms, please do not use the App.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">2. Service Description</h2>
                        <p className="text-gray-300">
                            Fixisy is a repair service management platform that enables businesses to track repairs,
                            manage customer information, and handle business operations. The App provides features
                            including but not limited to repair tracking, customer management, and payment processing.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">3. User Accounts</h2>
                        <p className="text-gray-300">
                            3.1. You must register for an account to use the App.<br />
                            3.2. You are responsible for maintaining the confidentiality of your account credentials.<br />
                            3.3. You agree to provide accurate and complete information during registration.<br />
                            3.4. You are solely responsible for all activities that occur under your account.<br />
                            3.5. Account Deletion:<br />
                            - You have the right to delete your account and personal information at any time through the App's settings<br />
                            - Personal data will be permanently deleted within 30 days<br />
                            - Business records and repair history will be retained as required by law and legitimate business purposes<br />
                            - You can request a copy of your data before deletion
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">4. Privacy and Data Protection</h2>
                        <p className="text-gray-300">
                            4.1. We collect and process personal data in accordance with our Privacy Policy.<br />
                            4.2. You agree to handle customer data in compliance with applicable data protection laws.<br />
                            4.3. You must obtain necessary consents from customers for data collection and processing.<br />
                            4.4. Data Collection and Permissions:<br />
                            - We collect only the data necessary for app functionality<br />
                            - We do not collect data for tracking or advertising purposes<br />
                            - We request specific permissions for device features (e.g., photo library access for repair documentation)<br />
                            - All permissions can be managed through your device settings<br />
                            - You can revoke permissions at any time through device settings
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">5. Service Usage</h2>
                        <p className="text-gray-300">
                            5.1. You agree to use the App only for lawful purposes.<br />
                            5.2. You will not attempt to interfere with the App's functionality or security.<br />
                            5.3. You will not use the App to store or transmit harmful code or malware.<br />
                            5.4. Platform-Specific Requirements:<br />
                            - iOS users must comply with Apple's App Store guidelines<br />
                            - All app features are designed for the iOS platform<br />
                            - App functionality is optimized for iOS devices<br />
                            - Screenshots and content reflect actual iOS app experience<br />
                            - User interface follows iOS design guidelines
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">6. Payment Terms</h2>
                        <p className="text-gray-300">
                            6.1. Some features may require payment of fees.<br />
                            6.2. All fees are non-refundable unless required by law.<br />
                            6.3. We reserve the right to modify our pricing with reasonable notice.<br />
                            6.4. Payment Information:<br />
                            - We use secure payment processors<br />
                            - Payment data is encrypted and protected<br />
                            - We do not store complete payment card information
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">7. Termination</h2>
                        <p className="text-gray-300">
                            7.1. We reserve the right to suspend or terminate your account for violations of these terms.<br />
                            7.2. Account Deletion Process:<br />
                            For Customer Accounts:<br />
                            - Upon deletion, you permanently lose app access<br />
                            - Repair businesses retain access to your repair history<br />
                            - New accounts won't have access to previous repair records<br />
                            - Contact details and repair history remain for record-keeping<br />
                            <br />
                            For Business Accounts:<br />
                            - Upon deletion, you permanently lose app access<br />
                            - Customers retain access to their repair records<br />
                            - New accounts won't have access to previous records<br />
                            - Business details and repair records remain for record-keeping<br />
                            <br />
                            7.3. Data Retention:<br />
                            - Account access is permanently removed upon deletion<br />
                            - Historical records are maintained for legitimate business purposes<br />
                            - Transaction records are kept as required by law<br />
                            - You can request a data export before deletion
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">8. Data Portability</h2>
                        <p className="text-gray-300">
                            8.1. You have the right to export your data from the App.<br />
                            8.2. Data Export Process:<br />
                            - Submit a request through our support site (https://fixisy.com/support)<br />
                            - Or email your request to support@fixisy.com<br />
                            - Requests will be processed within 30 days<br />
                            8.3. Data export includes:<br />
                            - Business and customer information<br />
                            - Repair records and history<br />
                            - Transaction data<br />
                            8.4. Export Format:<br />
                            - Data will be provided in a commonly used format (e.g., CSV, JSON)<br />
                            - Large exports may be split into multiple files<br />
                            - Secure download links will be provided via email
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">9. Changes to Terms</h2>
                        <p className="text-gray-300">
                            We reserve the right to modify these terms at any time. We will notify users of significant
                            changes. Continued use of the App after changes constitutes acceptance of the new terms.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">10. Governing Law and Jurisdiction</h2>
                        <p className="text-gray-300">
                            These Terms and Conditions are governed by the laws of India. Any dispute, claim, or complaint
                            arising out of or relating to the App or these terms—including service issues, billing disputes,
                            or account matters—shall be subject to the exclusive jurisdiction of the courts at{' '}
                            <strong className="text-white">Giridih, Jharkhand</strong>. You agree to submit to the personal
                            jurisdiction of those courts for the resolution of such matters.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">11. Contact Information</h2>
                        <p className="text-gray-300">
                            For questions about these Terms and Conditions, please contact Kifayti Retail Private Limited
                            (Fixisy) at:<br />
                            Email: legal@fixisy.com<br />
                            For general support: support@fixisy.com
                        </p>
                    </section>
                </div>
            </main>
        </div>
    );
};

export default TermsAndConditions; 